import { isAllowedCoverImageType } from './cover-image-process.js';

/** 候选下载尺寸（裁剪输出仍由 ImageCropDialog 控制到 400） */
const AVATAR_FETCH_SIZE = 256;
/** 预取池容量：后台尽量填满 10 张 */
export const RANDOM_AVATAR_POOL_SIZE = 10;
/** 裁剪弹窗展示数量（与池一致；未到位的用预占位） */
export const RANDOM_AVATAR_DISPLAY_COUNT = 10;

function randomSeed() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * 三源并行参与（不是 DiceBear 失败才走慢源）：
 * - DiceBear：程序生成插画，MIT
 * - Lorem Picsum：Unsplash 照片
 * - RoboHash：程序生成机器人头像
 * 慢源只影响「晚填 / 少填」，不阻塞弹窗打开。
 */
const RANDOM_AVATAR_SOURCES = [
  {
    id: 'dicebear',
    buildUrl: () =>
      `https://api.dicebear.com/9.x/adventurer/png?seed=${encodeURIComponent(
        randomSeed()
      )}&size=${AVATAR_FETCH_SIZE}`,
  },
  {
    id: 'picsum',
    buildUrl: () =>
      `https://picsum.photos/${AVATAR_FETCH_SIZE}/${AVATAR_FETCH_SIZE}?random=${randomSeed()}`,
  },
  {
    id: 'robohash',
    buildUrl: () =>
      `https://robohash.org/${encodeURIComponent(
        randomSeed()
      )}.png?size=${AVATAR_FETCH_SIZE}x${AVATAR_FETCH_SIZE}&set=set1`,
  },
];

function shuffleInPlace(list) {
  for (let i = list.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
}

function resolveImageType(blob) {
  const type = String(blob?.type ?? '').toLowerCase();
  if (isAllowedCoverImageType(type)) return type;
  if (type.startsWith('image/')) return 'image/png';
  if (blob?.size > 0) return 'image/jpeg';
  return '';
}

async function fetchAvatarFile(url) {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) {
    throw new Error('RANDOM_AVATAR_FETCH_FAILED');
  }
  const blob = await res.blob();
  const type = resolveImageType(blob);
  if (!type) {
    throw new Error('RANDOM_AVATAR_TYPE');
  }
  const ext = type === 'image/png' ? 'png' : 'jpg';
  return new File([blob], `random-avatar.${ext}`, { type, lastModified: Date.now() });
}

/** 单张：随机选一个源（三源都可能被抽到） */
export async function fetchRandomAvatarFile() {
  const source =
    RANDOM_AVATAR_SOURCES[Math.floor(Math.random() * RANDOM_AVATAR_SOURCES.length)];
  return fetchAvatarFile(source.buildUrl());
}

/** 按空缺补拉的最多轮数，避免全源挂死循环 */
const FILL_RETRY_ROUNDS = 8;

/**
 * 三源打散后并发拉取；谁先到谁经 onFile 填入。
 * 本轮有失败则只按空缺再补一轮，尽量凑满 count；一张都没有才抛错。
 * @param {number} [count]
 * @param {{ onFile?: (file: File) => void }} [options]
 */
export async function fetchRandomAvatarFiles(count = RANDOM_AVATAR_DISPLAY_COUNT, options = {}) {
  const size = Math.max(1, Number(count) || RANDOM_AVATAR_DISPLAY_COUNT);
  const onFile = typeof options.onFile === 'function' ? options.onFile : null;
  const files = [];

  for (let round = 0; round < FILL_RETRY_ROUNDS && files.length < size; round += 1) {
    const need = size - files.length;
    // 轮询保证三源都参与，再 shuffle 打乱顺序
    const assignments = shuffleInPlace(
      Array.from({ length: need }, (_, i) => RANDOM_AVATAR_SOURCES[i % RANDOM_AVATAR_SOURCES.length])
    );
    const before = files.length;

    await Promise.allSettled(
      assignments.map(async (source) => {
        try {
          const file = await fetchAvatarFile(source.buildUrl());
          if (files.length >= size) return null;
          files.push(file);
          onFile?.(file);
          return file;
        } catch {
          return null;
        }
      })
    );

    // 一整轮零成功：再试无意义（源大概率全挂）
    if (files.length === before) break;
  }

  if (!files.length) {
    throw new Error('RANDOM_AVATAR_FETCH_FAILED');
  }
  return files;
}

/** @type {File[]} */
let avatarPool = [];
/** @type {Promise<void>|null} */
let refillPromise = null;

/**
 * 后台异步补满预取池（目标 10）。打开编辑资料时调用，不挡 UI。
 * @param {number} [target]
 */
export function prefetchRandomAvatarPool(target = RANDOM_AVATAR_POOL_SIZE) {
  const need = Math.max(0, Math.floor(target) - avatarPool.length);
  if (need <= 0) return Promise.resolve();
  if (refillPromise) return refillPromise;

  refillPromise = (async () => {
    try {
      const files = await fetchRandomAvatarFiles(need);
      avatarPool.push(...files);
    } catch (err) {
      console.warn('[random-avatar] prefetch failed', err);
    } finally {
      refillPromise = null;
    }
  })();

  return refillPromise;
}

/**
 * 优先从池取；不足再三源并发补，并按空缺续补直到凑满。
 * 每张就绪 onFile（渐进填预占位）。
 * @param {number} [count]
 * @param {{ onFile?: (file: File) => void }} [options]
 */
export async function takeOrFetchRandomAvatars(
  count = RANDOM_AVATAR_DISPLAY_COUNT,
  options = {}
) {
  const size = Math.max(1, Number(count) || RANDOM_AVATAR_DISPLAY_COUNT);
  const onFile = typeof options.onFile === 'function' ? options.onFile : null;
  const collected = [];

  const fromPool = avatarPool.splice(0, Math.min(size, avatarPool.length));
  for (const file of fromPool) {
    collected.push(file);
    onFile?.(file);
  }

  const need = size - collected.length;
  if (need > 0) {
    await fetchRandomAvatarFiles(need, {
      onFile: (file) => {
        collected.push(file);
        onFile?.(file);
      },
    });
  }

  void prefetchRandomAvatarPool(RANDOM_AVATAR_POOL_SIZE);

  if (!collected.length) {
    throw new Error('RANDOM_AVATAR_FETCH_FAILED');
  }
  return collected;
}
